// 로그인 상태 hook — Supabase 세션을 구독해 로그인/로그아웃을 실시간 반영.
// - getSession(): 앱 시작 시 저장된 세션 1회 복원
// - onAuthStateChange: 로그인·로그아웃·토큰 갱신 시 자동 상태 갱신
import { useEffect, useRef, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "./supabase";
import { getSessionSafe } from "./authSession";
import { apiFetch } from "./api";
import { invalidate } from "./prefetch";
import { E2E_MOCK, E2E_FAKE_SESSION } from "./e2eMock";
import { markLoginQuestOnce } from "../ui/dailyQuestEffects";

export function useAuth() {
  const [session, setSession] = useState<Session | null>(null);
  // loading: 세션 복원이 끝나기 전 — UI 깜빡임(미로그인→로그인) 방지용
  const [loading, setLoading] = useState(true);
  const qc = useQueryClient();
  // 직전 로그인 user.id — 계정이 바뀌었는지(로그아웃/계정전환) 판별용
  const prevUserIdRef = useRef<string | null>(null);

  useEffect(() => {
    // E2E mock — 가짜 세션으로 로그인 가드 통과 (실 supabase 구독 스킵)
    if (E2E_MOCK) {
      setSession(E2E_FAKE_SESSION);
      setLoading(false);
      return;
    }
    // ⚠ 2026-07-25: `supabase.auth.getSession()` 직접 호출은 오프라인에서 무한 대기할 수 있어
    //   loading=true 가 풀리지 않고 앱이 시작 화면에 고착됐다(경로 상세는 [[authSession.ts]]).
    //   getSessionSafe는 타임아웃 시 저장소에 남은 세션으로 복구하므로 오프라인 부팅에서도
    //   로그인 상태가 유지된다(세션을 null로 돌려주면 로그인 화면으로 튕기는 회귀가 생김).
    getSessionSafe().then(({ session: restored }) => {
      prevUserIdRef.current = restored?.user?.id ?? null;
      setSession(restored);
      setLoading(false);
      // N-41: 모바일에 login 슬롯 기록이 0건이라 3슬롯 완주가 불가능했다.
      //   세션이 확정된 이 시점이 "오늘 앱을 켰다"의 서버 권위 기록점이다(하루 1회).
      void markLoginQuestOnce(restored?.user?.id);
    });
    // 로그인/로그아웃 이벤트 구독 — 콜백이 세션을 최신으로 유지
    const { data: sub } = supabase.auth.onAuthStateChange((event, s) => {
      const newUserId = s?.user?.id ?? null;
      // ⚠ 계정 전환/로그아웃 시 이전 계정의 react-query 캐시를 전역 무효화한다(2026-06-21).
      //   기존엔 SIGNED_OUT에서 ['me']만 removeQueries → sim/portfolio/trades 등 다른 쿼리는
      //   잔존했다. 그 결과 로그아웃→다른 계정 신규 로그인 직후 이전 계정의 모의투자 금액이
      //   잠깐 표시되는 버그(같은 기기 타인 정보 노출)가 발생. user.id가 바뀐 경우에만 clear해
      //   같은 user의 토큰 갱신·포커스 복귀(SIGNED_IN 재발생)에선 캐시를 살린다(불필요한 깜빡임 방지).
      //   prevUserIdRef가 null인 앱 시작(INITIAL_SESSION) 복원 시엔 clear하지 않는다.
      if (prevUserIdRef.current !== null && prevUserIdRef.current !== newUserId) {
        qc.clear();
        // ⚠ SimScreen·HomeScreen은 react-query가 아니라 prefetch.ts의 공유 캐시(store Map)를 쓴다
        //   → qc.clear()로는 안 비워져 계정전환 후에도 이전 계정 모의투자 포트폴리오가 잔존했다.
        //   invalidate()(인자 없으면 store/inflight 전체 clear)로 그 캐시까지 비운다(이번 핵심).
        invalidate();
        // 사용자 데이터를 담은 localStorage 캐시도 계정전환 시 제거(react-query 밖이라 qc.clear 미적용).
        //  ⚠ 새 user-scoped 캐시 키를 추가하면 여기에도 등록할 것(아니면 계정전환 시 타인 정보 잔존).
        try {
          localStorage.removeItem("sotda-review-cache-swing"); // 포트폴리오 AI 복기 리포트
          localStorage.removeItem("sotda-fm-pending-memo");    // 폐지수집소 작성중 메모
          localStorage.removeItem("sotda-debate-pending");      // 진행중 AI 토론 id
        } catch { /* localStorage 불가 환경 무시 */ }
      } else if (prevUserIdRef.current === null && newUserId && event === "SIGNED_IN") {
        // 첫 로그인(비로그인→로그인): 비로그인 시절 빈/공유 sim 캐시(prefetch store)를 비워
        //   홈 총자산·포트폴리오가 로그인 직후 즉시 갱신되게 한다 (5번 2026-06-24).
        //   INITIAL_SESSION(앱 시작 세션 복원)은 제외 — 그땐 비울 사용자 캐시가 없음.
        // ⚠ 2026-06-25: 비로그인 시절 미인증 응답(mbti emptyPayload code="" 등 staleTime Infinity)이
        //   React Query 캐시에 고착 → 로그인 후에도 'MBTI 진단 전'이 유지되는 버그.
        //   prefetch store뿐 아니라 qc도 무효화해 로그인 데이터로 refetch되게 한다.
        qc.invalidateQueries();
        invalidate();
      }
      prevUserIdRef.current = newUserId;
      setSession(s);
      // 로그인 즉시 me prefetch → 마이탭 진입 시 캐시 hit (빈 공간 깜빡임 방지)
      if (event === "SIGNED_IN") {
        qc.prefetchQuery({ queryKey: ["me"], queryFn: () => apiFetch("/api/mobile/me") });
        // N-41: 새로 로그인한 계정도 그날의 login 슬롯을 받아야 한다(앱 시작 복원과 별개 경로).
        void markLoginQuestOnce(newUserId);
      }
    });
    return () => sub.subscription.unsubscribe();
  }, [qc]);

  return { session, user: session?.user ?? null, loading };
}
